import { UUID } from '@beelday/common'
import {
	PollingMultipleChoiceAnswer,
	PollingMultipleChoiceAnsweredAnswer,
} from './multiple-choice-polling-models'

export type PollResultRow = {
	answerId: UUID
	text: string
	votes: number
	/** Of those who answered, rounded; 0 before anybody has. */
	percent: number
	/** Has the most votes - with any it ties with - once there are any. */
	leading: boolean
}

export type PollResults = {
	rows: PollResultRow[]
	/** Who answered, each once however many answers they picked. */
	respondents: number
}

/**
 * How the room answered a multiple-choice poll, an answer at a time in the
 * poll's own order. The shares are of those who answered, so a poll that
 * takes more than one answer can add up to more than 100%.
 */
export const pollResults = (
	possibleAnswers: PollingMultipleChoiceAnswer[],
	answers: PollingMultipleChoiceAnsweredAnswer[] = []
): PollResults => {
	const respondents = new Set(answers.map(answer => answer.userId)).size
	const votes = possibleAnswers.map(
		possible =>
			new Set(
				answers
					.filter(answer => answer.answerId === possible.answerId)
					.map(answer => answer.userId)
			).size
	)
	const most = Math.max(0, ...votes)
	return {
		respondents,
		rows: possibleAnswers.map((possible, index) => ({
			answerId: possible.answerId,
			text: possible.text,
			votes: votes[index],
			percent: respondents ? Math.round((votes[index] / respondents) * 100) : 0,
			leading: most > 0 && votes[index] === most,
		})),
	}
}

/** More answers than this and columns get too narrow to read from the back of the room. */
export const MAX_COLUMNS = 6

/** An answer longer than this needs a row of its own. */
export const MAX_COLUMN_LABEL = 48

export type PollChart = 'columns' | 'bars'

/**
 * Standing columns, which fill the card down to its foot, while every answer
 * fits under its own column; lying bars, a row each, otherwise - as the
 * online chart does for long answers.
 */
export const pollChart = (rows: PollResultRow[]): PollChart =>
	rows.length <= MAX_COLUMNS &&
	rows.every(row => row.text.length <= MAX_COLUMN_LABEL)
		? 'columns'
		: 'bars'
